import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import MessagingSystem from '../messaging/MessagingSystem';
import ImageUpload from '../ImageUpload';
import PropertyListingForm, { EMPTY_LISTING } from './PropertyListingForm';
import VibeMate from '../VibeMate';
import { useAuth } from '../../context/AuthContext';
import RewardsTab, { PointsBadge } from '../rewards/RewardsTab';
import RentReceiptsTab from '../rewards/RentReceiptsTab';
import VerificationBanner from '../VerificationBanner';
import VerificationFlow from '../VerificationFlow';
import { trackEvent } from '../../data/trackEvent';
import {
  Home, MessageSquare, User, Settings, Search, Plus, Bell,
  LogOut, MapPin, Calendar, DollarSign, Building2,
  Shield, Users, CheckCircle, Upload, Star, Heart, Trash2, Trophy,
  Eye, Copy, Check, ExternalLink, Menu
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Private listing shareable link component ──────────────────────────────────
function StudentPrivateLink({ token }) {
  const [copied, setCopied] = React.useState(false);
  const url = `https://rentalx.io/listing/private/${token}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 mb-2">
      <p className="text-[10px] font-bold text-amber-700 mb-1">🔒 Private link</p>
      <div className="flex items-center gap-1.5">
        <code className="flex-1 text-[10px] text-amber-800 bg-white border border-amber-200 rounded-lg px-2 py-1 truncate font-mono">
          {url}
        </code>
        <button onClick={copy}
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white transition-all"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}


// ── Profile Tab ────────────────────────────────────────────────────────────────
function ProfileTab({ user, token }) {
  const [fullName, setFullName] = React.useState(user?.full_name || '');
  const [phone, setPhone] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ full_name: fullName, phone, bio }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      }
    } catch { /* silent */ }
    setSaving(false);
  };

  const initials = (user?.full_name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">My Profile</h2>
        <p className="text-sm text-gray-400">Your public profile visible to landlords and housemates.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        {/* Avatar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-xl font-black flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            {initials}
          </div>
          <div>
            <p className="font-bold text-gray-900">{user?.full_name}</p>
            <p className="text-sm text-gray-400">{user?.email}</p>
            <p className="text-xs text-gray-400 mt-0.5 capitalize">{user?.role}</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
            <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+61 4XX XXX XXX" type="tel" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">About Me</label>
            <textarea value={bio} onChange={e => setBio(e.target.value)}
              placeholder="A short bio visible to landlords and potential housemates..."
              rows={3} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25 resize-none" />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <Button type="submit" disabled={saving} className="text-white hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
            {saved && <span className="text-sm text-green-600 font-medium">Saved successfully</span>}
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab({ user, token, onLogout }) {
  const [notifEmail, setNotifEmail] = React.useState(true);
  const [notifListings, setNotifListings] = React.useState(true);
  const [notifMessages, setNotifMessages] = React.useState(true);
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  const Toggle = ({ label, value, onChange, desc }) => (
    <div className="flex items-center justify-between py-3.5 border-b border-gray-100 last:border-0">
      <div>
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      <button onClick={() => onChange(!value)}
        className={`w-11 h-6 rounded-full transition-colors flex-shrink-0 relative ${value ? '' : 'bg-gray-200'}`}
        style={value ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${value ? 'right-0.5' : 'left-0.5'}`} />
      </button>
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Settings</h2>
        <p className="text-sm text-gray-400">Manage your notifications and account preferences.</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <p className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-1">Notifications</p>
        <Toggle label="Email notifications" desc="Receive important updates via email" value={notifEmail} onChange={setNotifEmail} />
        <Toggle label="New listing alerts" desc="Get notified when new listings match your requirements" value={notifListings} onChange={setNotifListings} />
        <Toggle label="Message notifications" desc="Get emailed when you receive a new message" value={notifMessages} onChange={setNotifMessages} />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <p className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">Account</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-semibold text-gray-800">Email address</p>
              <p className="text-xs text-gray-400">{user?.email}</p>
            </div>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified</span>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-gray-100">
            <div>
              <p className="text-sm font-semibold text-gray-800">Account role</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-red-200 p-6">
        <p className="text-sm font-bold text-red-600 uppercase tracking-wide mb-3">Danger Zone</p>
        {showLogoutConfirm ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-gray-600">Are you sure you want to sign out?</p>
            <Button size="sm" className="bg-red-500 text-white hover:bg-red-600" onClick={onLogout}>Yes, sign out</Button>
            <Button size="sm" variant="outline" onClick={() => setShowLogoutConfirm(false)}>Cancel</Button>
          </div>
        ) : (
          <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
            onClick={() => setShowLogoutConfirm(true)}>
            Sign out of this device
          </Button>
        )}
      </div>
    </div>
  );
}


const StudentDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showVerifyFlow, setShowVerifyFlow] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'requirements');

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
    trackEvent(token, 'tab_viewed', { tab: tabId, dashboard: 'student' });
  };
  const [requirements, setRequirements] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqSubmitting, setReqSubmitting] = useState(false);

  // Load requirements from real API
  useEffect(() => {
    if (!token) return;
    setReqLoading(true);
    async function loadReqs() {
      try {
        const r = await fetch(`${API}/api/requirements/my`, { headers: { Authorization: `Bearer ${token}` } });
        const data = r.ok ? await r.json() : [];
        setRequirements(Array.isArray(data) ? data : []);
      } catch { setRequirements([]); }
      setReqLoading(false);
    }
    loadReqs();
  }, [token]);
  const [myListings, setMyListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [newRequirement, setNewRequirement] = useState({
    title: '',
    budget: '',
    location: '',
    roomType: '',
    moveInDate: '',
    duration: '',
    description: '',
    preferences: []
  });
  const [kycStatus, setKycStatus] = useState('unverified'); // unverified | pending | verified
  const [kycFile, setKycFile] = useState(null);

  // VibeMate state
  const [vibeProfile, setVibeProfile] = useState({
    schedule: '', noise: '', cleanliness: '', social: '', diet: '', interests: ''
  });
  const [vibeMatches, setVibeMatches] = useState([]);
  const [vibeSubmitted, setVibeSubmitted] = useState(false);

  const MOCK_VIBE_MATCHES = [
    { name: 'Aisha T.', uni: 'University of Melbourne', score: 94, tags: ['Early bird', 'Quiet study', 'Vegetarian', 'Gym'], avatar: 'A' },
    { name: 'Daniel K.', uni: 'RMIT University', score: 89, tags: ['Night owl', 'Social', 'Gamer', 'Clean'], avatar: 'D' },
    { name: 'Priya S.', uni: 'Monash University', score: 86, tags: ['Morning person', 'Yoga', 'Cooking', 'No parties'], avatar: 'P' },
  ];

  const handleVibeSubmit = (e) => {
    e.preventDefault();
    setVibeMatches(MOCK_VIBE_MATCHES);
    setVibeSubmitted(true);
  };

  const handleKycSubmit = () => {
    if (kycFile) setKycStatus('pending');
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const [newListing, setNewListing] = useState({ ...EMPTY_LISTING });
  const [listingSubmitting, setListingSubmitting] = useState(false);
  const [listingSuccess, setListingSuccess] = useState(false);
  const [listingDraftId, setListingDraftId] = useState(null);
  const [listingDraftSaved, setListingDraftSaved] = useState(false);
  const [editingListingId, setEditingListingId] = useState(null);
  const [editingListingStatus, setEditingListingStatus] = useState(null);
  const [showListingForm, setShowListingForm] = useState(false);

  // Load student's own listings from real API
  useEffect(() => {
    if (!token) return;
    setLoadingListings(true);
    async function loadListings() {
      try {
        const r = await fetch(`${API}/api/properties/my`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        setMyListings(Array.isArray(data) ? data : []);
      } catch { setMyListings([]); }
      setLoadingListings(false);
    }
    loadListings();
  }, [token]);

  const handleListingSubmit = async (form) => {
    setListingSubmitting(true);
    const isUpdate = editingListingId || listingDraftId;
    const method = isUpdate ? 'PUT' : 'POST';
    const url = isUpdate ? `${API}/api/properties/${editingListingId || listingDraftId}` : `${API}/api/properties`;
    const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', ') || '',
          price_per_week: parseFloat(form.price_per_week),
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: 'pending_review',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (isUpdate) {
          setMyListings(prev => prev.map(p => p.id === (editingListingId || listingDraftId) ? data : p));
        } else {
          setMyListings(prev => [data, ...prev]);
        }
        setListingSuccess(true);
        setShowListingForm(false);
        setEditingListingId(null);
        setEditingListingStatus(null);
        setListingDraftId(null);
        setListingDraftSaved(false);
        setTimeout(() => setListingSuccess(false), 3000);
      }
    } catch { /* silent */ }
    finally { setListingSubmitting(false); }
  };

  // Submit for Review: only available when editing a draft — mirrors LandlordDashboard's handleSubmitForReview
  const handleListingSubmitForReview = async (form) => {
    setListingSubmitting(true);
    const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
    try {
      const res = await fetch(`${API}/api/properties/${editingListingId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', ') || '',
          price_per_week: parseFloat(form.price_per_week),
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: 'pending_review',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setMyListings(prev => prev.map(p => p.id === editingListingId ? data : p));
        setListingSuccess(true);
        setShowListingForm(false);
        setEditingListingId(null);
        setEditingListingStatus(null);
        setListingDraftId(null);
        setListingDraftSaved(false);
        setTimeout(() => setListingSuccess(false), 3000);
      }
    } catch { /* silent */ }
    finally { setListingSubmitting(false); }
  };

  const handleListingDraft = async (form) => {
    const isUpdate = editingListingId || listingDraftId;
    const method = isUpdate ? 'PUT' : 'POST';
    const url = isUpdate ? `${API}/api/properties/${editingListingId || listingDraftId}` : `${API}/api/properties`;
    const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
    try {
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', ') || '',
          price_per_week: parseFloat(form.price_per_week) || 0,
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: 'draft',
        }),
      });
      const data = await res.json();
      if (res.ok && !isUpdate) {
        setListingDraftId(data.id);
        setMyListings(prev => [data, ...prev]);
      }
      setListingDraftSaved(true);
    } catch { /* silent */ }
  };

  const handleDeleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    const res = await fetch(`${API}/api/properties/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok || res.status === 204) {
      setMyListings(prev => prev.filter(p => p.id !== id));
    }
  };

  const handleRequirementSubmit = async (e) => {
    e.preventDefault();
    setReqSubmitting(true);
    try {
      const res = await fetch(`${API}/api/requirements`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newRequirement.title,
          budget: parseFloat(newRequirement.budget) || 0,
          location: newRequirement.location,
          roomType: newRequirement.roomType,
          moveInDate: newRequirement.moveInDate,
          duration: newRequirement.duration,
          description: newRequirement.description,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setRequirements(prev => [data, ...prev]);
        setNewRequirement({ title: '', budget: '', location: '', roomType: '', moveInDate: '', duration: '', description: '', preferences: [] });
      }
    } catch { /* silent */ }
    finally { setReqSubmitting(false); }
  };

  const handleDeleteRequirement = async (id) => {
    if (!window.confirm('Delete this requirement?')) return;
    const res = await fetch(`${API}/api/requirements/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok || res.status === 204) {
      setRequirements(prev => prev.filter(r => r.id !== id));
    }
  };

  const sidebarGroups = [
    {
      label: 'Renting',
      items: [
        { id: 'requirements',  label: 'My Requirements',   icon: MessageSquare },
        { id: 'my-listings',   label: 'My Listings',       icon: Building2 },
        { id: 'my-rent',       label: 'My Rent',           icon: DollarSign },
        { id: 'saved',         label: 'Saved Properties',  icon: Home },
        { id: 'applications',  label: 'Applications',      icon: Search },
        { id: 'messages',      label: 'Messages',          icon: MessageSquare, badge: 2 },
      ],
    },
    {
      label: 'Features',
      items: [
        { id: 'vibe-mate',  label: 'VibeMate™', icon: Users },
        { id: 'rewards',    label: 'Rewards',   icon: Trophy },
      ],
    },
    {
      label: 'Account',
      items: [
        { id: 'verify',   label: 'Verify ID', icon: Shield },
        { id: 'profile',  label: 'Profile',   icon: User },
        { id: 'settings', label: 'Settings',  icon: Settings },
      ],
    },
  ];

  const TAB_LABELS = {
    requirements: 'My Requirements', 'my-listings': 'My Listings',
    'my-rent': 'My Rent', 'vibe-mate': 'VibeMate™', verify: 'Verify ID',
    saved: 'Saved Properties', applications: 'Applications',
    messages: 'Messages', rewards: 'Rewards', profile: 'Profile', settings: 'Settings',
  };

  const userInitials = user?.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'ST';

  const renderRequirementsTab = () => (
    <div className="space-y-5">
      {/* Intro banner */}
      <div className="flex items-start gap-4 bg-[#094f6f]/5 border border-[#094f6f]/15 rounded-2xl px-5 py-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <div>
        <p className="font-bold text-[#094f6f] text-sm">Post what you need, let the world come to you</p>
          <p className="text-xs text-gray-500 mt-0.5">Requirements are visible to verified accommodation providers.</p>
        </div>
      </div>

      {/* Form card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
            <Plus className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm">New accommodation requirement</p>
            <p className="text-xs text-gray-400">Takes under a minute</p>
          </div>
        </div>
        <form onSubmit={handleRequirementSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Title <span className="text-gray-400 font-normal">What are you looking for?</span>
            </label>
            <Input
              value={newRequirement.title}
              onChange={e => setNewRequirement({ ...newRequirement, title: e.target.value })}
              placeholder="e.g. Looking for a shared apartment near UNSW"
              required
              className="focus:ring-[#094f6f]/30"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Location</label>
              <Input
                value={newRequirement.location}
                onChange={e => setNewRequirement({ ...newRequirement, location: e.target.value })}
                placeholder="e.g. Parramatta NSW"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Room type</label>
              <Select value={newRequirement.roomType} onValueChange={v => setNewRequirement({ ...newRequirement, roomType: v })}>
                <SelectTrigger><SelectValue placeholder="Any type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private-room">Private room</SelectItem>
                  <SelectItem value="shared-room">Shared room</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="1-bedroom">1-bedroom</SelectItem>
                  <SelectItem value="2-bedroom">2-bedroom</SelectItem>
                  <SelectItem value="entire-apartment">Entire apartment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Move-in date</label>
              <Input type="date" value={newRequirement.moveInDate} onChange={e => setNewRequirement({ ...newRequirement, moveInDate: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Budget <span className="text-gray-400 font-normal">AUD per week</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">$</span>
                <Input type="number" value={newRequirement.budget} onChange={e => setNewRequirement({ ...newRequirement, budget: e.target.value })}
                  placeholder="e.g. 280" min="50" max="2000" className="pl-7" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Lease duration</label>
              <Select value={newRequirement.duration || ''} onValueChange={v => setNewRequirement({ ...newRequirement, duration: v })}>
                <SelectTrigger><SelectValue placeholder="Any duration" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-3">1–3 months</SelectItem>
                  <SelectItem value="3-6">3–6 months</SelectItem>
                  <SelectItem value="6-12">6–12 months</SelectItem>
                  <SelectItem value="12+">12+ months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description & preferences <span className="text-gray-400 font-normal">Lifestyle, special requirements, deal-breakers</span>
            </label>
            <textarea value={newRequirement.description} onChange={e => setNewRequirement({ ...newRequirement, description: e.target.value })}
              placeholder="e.g. Quiet study environment, close to public transport, no smokers, pet-friendly preferred..."
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 resize-none"
              required />
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-gray-400 flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> Visible to verified landlords only
            </span>
            <Button type="submit" disabled={reqSubmitting}
              className="flex items-center gap-2 text-white hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              {reqSubmitting ? 'Posting…' : <><MapPin className="w-4 h-4" /> Post requirement</>}
            </Button>
          </div>
        </form>
      </div>

      {/* Posted requirements */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <span className="font-bold text-gray-900 text-sm">Your posted requirements</span>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)', color: 'white' }}>
            {requirements.length} active
          </span>
        </div>
        {reqLoading ? (
          <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : requirements.length === 0 ? (
          <div className="py-12 text-center px-6">
            <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
              <MessageSquare className="w-6 h-6 text-gray-400" />
            </div>
            <p className="font-semibold text-gray-700 mb-1">No requirements posted yet</p>
            <p className="text-sm text-gray-400 mb-3">Post your first requirement above and start receiving property matches from verified landlords.</p>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2cbdc4] bg-[#2cbdc4]/10 px-3 py-1.5 rounded-full">
              <Star className="w-3 h-3" /> VibeMate members get priority placement
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {requirements.map(req => (
              <div key={req.id} className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="w-2 h-2 rounded-full bg-[#2cbdc4] mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm mb-2">{req.title}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {req.location && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        <MapPin className="w-3 h-3" /> {req.location}
                      </span>
                    )}
                    {req.roomType && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        <Home className="w-3 h-3" /> {req.roomType}
                      </span>
                    )}
                    {req.budget && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        <DollarSign className="w-3 h-3" /> A${req.budget}/wk
                      </span>
                    )}
                    {req.moveInDate && (
                      <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        <Calendar className="w-3 h-3" /> {new Date(req.moveInDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                  {req.description && <p className="text-xs text-gray-500 line-clamp-2">{req.description}</p>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" className="text-xs h-8">Edit</Button>
                  <Button size="sm" variant="outline" className="text-xs h-8 text-red-500 hover:text-red-600"
                    onClick={() => handleDeleteRequirement(req.id)}>Remove</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderMyListingsTab = () => (
    <div className="space-y-6">
      {listingSuccess && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          <CheckCircle className="w-5 h-5" />
          Listing published! It&apos;s now visible on the homepage.
        </div>
      )}

      {/* Show form or listings list */}
      {showListingForm ? (
        <PropertyListingForm
          initialValues={newListing}
          editingId={editingListingId}
          editingStatus={editingListingStatus}
          onSubmit={handleListingSubmit}
          onSaveDraft={handleListingDraft}
          onSubmitForReview={handleListingSubmitForReview}
          onCancel={() => { setShowListingForm(false); setEditingListingId(null); setEditingListingStatus(null); setListingDraftId(null); setListingDraftSaved(false); }}
          submitting={listingSubmitting}
          submitSuccess={false}
          draftSaved={listingDraftSaved}
          title={editingListingId ? 'Edit Your Listing' : 'Post Your Room / Accommodation'}
        />
      ) : (
        <>
          {/* Add new button */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">My Listings</h2>
              <p className="text-sm text-gray-500">Subletting, flatmate search, or room share? List it here.</p>
            </div>
            <Button onClick={() => { setNewListing({ ...EMPTY_LISTING }); setShowListingForm(true); }}
              className="text-white" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
              <Plus className="w-4 h-4 mr-2" /> Post a Listing
            </Button>
          </div>

          {/* Posted listings */}
          {loadingListings ? (
            <div className="text-center py-8 text-gray-400">Loading your listings...</div>
          ) : myListings.length === 0 ? (
            <Card>
              <CardContent className="text-center py-10">
                <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-1">No listings posted yet.</p>
                <p className="text-sm text-gray-400">Have a room to share or sublet? Click &quot;Post a Listing&quot; above!</p>
              </CardContent>
            </Card>
          ) : (
            myListings.map((listing) => (
              <Card key={listing.id}>
                <CardContent className="p-5">
                  <div className="flex gap-4">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.name} className="w-24 h-20 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-24 h-20 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-8 h-8 text-sky-200" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-semibold text-gray-800">{listing.name}</h4>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          {listing.visibility === 'private' && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">🔒 Private</span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            listing.status === 'active' ? 'bg-green-100 text-green-700' :
                            listing.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                            listing.status === 'rejected' ? 'bg-red-100 text-red-600' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {listing.status === 'active' ? '✓ Live' :
                             listing.status === 'pending_review' ? '⏳ Pending Review' :
                             listing.status === 'rejected' ? '✕ Rejected' : listing.status}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />A${listing.price_per_week}/wk</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{listing.city}</span>
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{listing.room_type}</span>
                      </div>

                      {/* Private shareable link */}
                      {listing.visibility === 'private' && listing.private_token && (
                        <StudentPrivateLink token={listing.private_token} />
                      )}

                      <div className="flex gap-2 mt-2">
                        {/* View listing */}
                        <Button size="sm" variant="outline" className="text-xs"
                          onClick={() => navigate(`/property/${listing.id}`)}>
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs"
                          onClick={() => {
                            setNewListing({ ...EMPTY_LISTING, ...listing });
                            setEditingListingId(listing.id);
                            setEditingListingStatus(listing.status || null);
                            setListingDraftSaved(false);
                            setShowListingForm(true);
                          }}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs text-red-500" onClick={() => handleDeleteListing(listing.id)}>
                          <Trash2 className="w-3 h-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </>
      )}
    </div>
  );

  const renderVibeTab = () => <VibeMate />;

  // KYC role switcher state
  const [kycUserType, setKycUserType] = useState('student'); // 'student' | 'professional'
  const [profFiles, setProfFiles] = useState({ govId: null, employment: null });
  const [linkedIn, setLinkedIn] = useState('');
  const [abn, setAbn] = useState('');

  const renderVerifyTab = () => {
    const statusBanner = (label) => (
      <div className={`p-4 rounded-xl border flex items-center gap-3 ${
        kycStatus === 'verified' ? 'bg-green-50 border-green-200' :
        kycStatus === 'pending' ? 'bg-yellow-50 border-yellow-200' :
        'bg-gray-50 border-gray-200'
      }`}>
        {kycStatus === 'verified' && <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />}
        {kycStatus === 'pending' && <div className="w-6 h-6 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin flex-shrink-0" />}
        {kycStatus === 'unverified' && <Shield className="w-6 h-6 text-gray-400 flex-shrink-0" />}
        <div>
          <p className="font-semibold text-sm">
            {kycStatus === 'verified' ? `✅ Verified ${label}` :
             kycStatus === 'pending' ? '⏳ Verification Pending (1–2 business days)' : '⚠️ Not Yet Verified'}
          </p>
          <p className="text-xs text-gray-500">
            {kycStatus === 'verified' ? `Your ${label.toLowerCase()} status is confirmed. Verified badge shown on your profile.` :
             kycStatus === 'pending' ? "We're reviewing your documents. You'll receive an email once approved." :
             'Upload your documents to get verified.'}
          </p>
        </div>
      </div>
    );

    const fileUpload = (id, label, file, setFile) => (
      <div className="border-2 border-dashed border-sky-200 rounded-xl p-5 text-center hover:border-[#094f6f] transition-colors">
        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
        <p className="text-xs text-gray-400 mb-3">PDF, JPG, PNG · max 5MB</p>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} className="hidden" id={id} />
        <label htmlFor={id} className="cursor-pointer bg-[#094f6f] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#094f6f]/90 transition-colors">
          Choose File
        </label>
        {file && <p className="text-xs text-green-600 mt-2">✓ {file.name}</p>}
      </div>
    );

    return (
      <div className="space-y-5 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Identity Verification</h2>
            <p className="text-sm text-gray-500">Get verified to unlock full access and build trust with landlords</p>
          </div>
        </div>

        {/* Role switcher */}
        <div className="flex bg-gray-100 rounded-full p-1 w-fit">
          {[['student', '🎓 Student'], ['professional', '💼 Working Professional']].map(([val, label]) => (
            <button key={val} onClick={() => setKycUserType(val)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                kycUserType === val ? 'bg-[#094f6f] text-white shadow-sm' : 'text-gray-600 hover:text-[#094f6f]'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Status */}
        {statusBanner(kycUserType === 'student' ? 'Student' : 'Professional')}

        {kycStatus === 'unverified' && kycUserType === 'student' && (
          <Card>
            <CardHeader><CardTitle className="text-[#094f6f] text-base">Student Verification Documents</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {['Student ID Card', 'Enrolment Letter', 'Offer Letter', 'Academic Transcript'].map(doc => (
                  <div key={doc} className="flex items-center gap-2 p-3 rounded-lg bg-sky-50 border border-sky-100 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0" />{doc}
                  </div>
                ))}
              </div>
              <div className="border-2 border-dashed border-sky-200 rounded-xl p-6 text-center hover:border-[#094f6f] transition-colors">
                <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 mb-1">Upload one of the above documents</p>
                <p className="text-xs text-gray-400 mb-3">PDF, JPG, PNG · max 5MB</p>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setKycFile(e.target.files[0])} className="hidden" id="kyc-student-upload" />
                <label htmlFor="kyc-student-upload" className="cursor-pointer bg-[#094f6f] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#094f6f]/90 transition-colors">
                  Choose File
                </label>
                {kycFile && <p className="text-xs text-green-600 mt-2">✓ {kycFile.name}</p>}
              </div>
              <button onClick={handleKycSubmit} disabled={!kycFile}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold disabled:opacity-40 hover:bg-green-700 transition-colors">
                Submit for Verification
              </button>
              <p className="text-xs text-gray-400 text-center">Encrypted and used only for verification. Complies with Australia&apos;s Privacy Act 1988.</p>
            </CardContent>
          </Card>
        )}

        {kycStatus === 'unverified' && kycUserType === 'professional' && (
          <Card>
            <CardHeader><CardTitle className="text-[#094f6f] text-base">Professional Verification</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {/* Required docs */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Required Documents</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fileUpload('gov-id-upload', 'Government-issued ID (Passport or Driver\'s Licence)', profFiles.govId, (f) => setProfFiles(p => ({...p, govId: f})))}
                  {fileUpload('employment-upload', 'Employment Proof (Payslip, Employment Letter, or Contract)', profFiles.employment, (f) => setProfFiles(p => ({...p, employment: f})))}
                </div>
              </div>

              {/* Optional extras */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Optional (strengthens your profile)</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">LinkedIn Profile URL</label>
                    <input type="url" value={linkedIn} onChange={e => setLinkedIn(e.target.value)}
                      placeholder="https://linkedin.com/in/yourname"
                      className="w-full px-3 py-2.5 border border-sky-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">ABN <span className="text-gray-400">(self-employed / contractors)</span></label>
                    <input type="text" value={abn} onChange={e => setAbn(e.target.value)}
                      placeholder="e.g. 51 824 753 556"
                      className="w-full px-3 py-2.5 border border-sky-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                  </div>
                </div>
              </div>

              <button
                onClick={() => { if (profFiles.govId && profFiles.employment) setKycStatus('pending'); }}
                disabled={!profFiles.govId || !profFiles.employment}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold disabled:opacity-40 hover:bg-green-700 transition-colors">
                Submit for Verification
              </button>
              <p className="text-xs text-gray-400 text-center">Encrypted and used only for verification. Complies with Australia&apos;s Privacy Act 1988.</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'requirements':
        return renderRequirementsTab();
      case 'my-listings':
        return renderMyListingsTab();
      case 'saved':
        return <div className="text-center py-8">Saved Properties Coming Soon</div>;
      case 'applications':
        return <div className="text-center py-8">Applications Coming Soon</div>;
      case 'messages':
        return (
          <div className="h-96">
            <MessagingSystem userType="student" />
          </div>
        );
      case 'profile':
        return <ProfileTab user={user} token={token} />;
      case 'vibe-mate': return renderVibeTab();
      case 'my-rent': return <RentReceiptsTab token={token} />;
      case 'verify': return renderVerifyTab();
      case 'rewards': return <RewardsTab token={token} userRole={user?.role || 'student'} />;
      case 'settings':
        return <SettingsTab user={user} token={token} onLogout={handleLogout} />;
      default:
        return renderRequirementsTab();
    }
  };

  return (
    <>
    <div className="min-h-screen bg-[#f4f6f9] flex">

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}

      {/* ── Sidebar ── */}
      <aside className={`w-60 bg-white border-r border-gray-200 min-h-screen flex flex-col flex-shrink-0 fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="flex items-center gap-0 px-5 py-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.3rem', width: 'auto', marginLeft: '0.04em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </Link>
        </div>

        {/* Grouped nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {sidebarGroups.map(group => (
            <div key={group.label} className="mb-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-1.5">{group.label}</p>
              {group.items.map(item => (
                <button key={item.id} onClick={() => switchTab(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                    activeTab === item.id
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  style={activeTab === item.id ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                  <div className="flex items-center gap-2.5">
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </div>
                  {item.badge && (
                    <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${
                      activeTab === item.id ? 'bg-white/30 text-white' : 'bg-[#2cbdc4] text-white'
                    }`}>{item.badge}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {/* Logout at bottom */}
        <div className="px-3 pb-5 border-t border-gray-100 pt-3">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Topbar */}
        <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle"
              className="lg:hidden w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 flex-shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <span className="text-xs text-gray-400 hidden sm:inline">Dashboard / </span>
              <span className="text-sm font-bold text-gray-900 truncate">{TAB_LABELS[activeTab] || activeTab}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PointsBadge token={token} onClick={() => switchTab('rewards')} />
            <button className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors">
              <Bell className="w-4 h-4" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(m => !m)}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0 hover:opacity-80 transition-opacity"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                title={user?.full_name}>
                {userInitials}
              </button>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 top-11 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100 mb-1">
                    <p className="text-sm font-bold text-gray-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <button onClick={() => { switchTab('profile'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <User className="w-4 h-4" /> My Profile
                  </button>
                  <button onClick={() => { switchTab('rewards'); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                    <Trophy className="w-4 h-4" /> Rewards
                  </button>
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Verification banner */}
        <VerificationBanner token={token} userRole={user?.role} onVerifyClick={() => setShowVerifyFlow(true)} />

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          <div className="max-w-5xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </div>

      {showVerifyFlow && (
        <VerificationFlow token={token} onClose={() => setShowVerifyFlow(false)} onComplete={() => setShowVerifyFlow(false)} />
      )}
    </div>
    </>
  );
};

export default StudentDashboard;